// Cómo se llaman los mercados y las selecciones, en los tres idiomas.
//
// T3 de `odd/tasks/el-idioma-del-jugador.md`.
//
// LO QUE HABÍA: DOS DICCIONARIOS Y DOS TRADUCTORES
// -------------------------------------------------
// `MKT_ES` en `cuotasDeEvento.jsx` con 34 entradas, `MKT` en `Web.jsx` con 4
// —propio, distinto y solapado: el mismo `h2h` se llamaba "Ganador" en la app
// y "Ganador del partido" en el sitio—, más `etiquetaResultado` y
// `etiquetaCorta`, cada uno con su propia lista de casos. Cuatro lugares para
// la misma pregunta, y ninguno sabía del idioma del jugador.
//
// ESTO ES LA CAPA QUE DIBUJA, Y NADA MÁS
// ---------------------------------------
// La regla más importante del archivo. El texto de acá se MUESTRA; lo que
// viaja al boleto es el nombre crudo del emisor. `betslipPicks.js` resuelve la
// clave con `firstFilled(pick.nombre, …, pick.label)`, o sea que la etiqueta es
// el último repliegue: si una etiqueta traducida se cuela ahí, la clave de
// liquidación pasa a depender del idioma del que mira. El boleto se vendería
// igual —el prefijo le alcanza a la validación de venta— y fallaría recién al
// liquidar, quedando para que lo pague una persona a mano. Eso ya pasó acá con
// la etiqueta en español; con tres idiomas sería tres veces el mismo agujero.
//
// Por eso ninguna función de este archivo toca `nombre` ni `sel`, y hay una
// prueba que lo fija: el mismo nombre de emisor tiene que dar etiquetas
// DISTINTAS por idioma, que es justamente lo que vuelve imposible usarla como
// clave.
//
// EL NOMBRE DE UN EQUIPO NO SE TRADUCE
// -------------------------------------
// Es contenido, no etiqueta. "River Plate" se llama igual en inglés, y
// traducirlo sería inventarle otro nombre a un club. Lo mismo el `1 X 2`, que
// se lee igual en todos los idiomas en los que se apuesta.
//
// EL PORTUGUÉS ES EL DE BRASIL
// -----------------------------
// Que es el mercado nuestro y el que Content360 entrega como `pt_BR`. Importa
// en el vocabulario: los córners son "escanteios" y no "cantos", y los remates
// son "chutes". Un portugués de Portugal leería raro, y es la elección
// correcta igual.
//
// LAS TRADUCCIONES NO ESTÁN REVISADAS POR UN HABLANTE NATIVO. Son terminología
// estándar de apuestas, no una invención, pero antes de salir en vivo
// conviene que las lea alguien que apueste en inglés y en portugués.

import { PREDETERMINADO, idiomaSoportado } from "./idioma";

// Clave de mercado -> título por idioma (`t`) y orden en la pantalla (`o`).
//
// El orden NO es por idioma a propósito: es en qué secuencia se muestran los
// mercados, y esa decisión es de producto y la misma para todos. Que un
// jugador en inglés vea los mercados en otro orden no tendría ningún motivo.
export const MERCADOS = {
  // Principales
  h2h: { o: 1, t: {
    es: "Ganador", en: "Match Winner", pt: "Vencedor" } },
  h2h_3_way: { o: 2, t: {
    es: "Ganador (1X2)", en: "Match Winner (1X2)", pt: "Vencedor (1X2)" } },
  totals: { o: 3, t: {
    es: "Más / Menos goles", en: "Over / Under Goals",
    pt: "Mais / Menos gols" } },
  spreads: { o: 4, t: {
    es: "Hándicap", en: "Handicap", pt: "Handicap" } },
  double_chance: { o: 5, t: {
    es: "Doble oportunidad", en: "Double Chance", pt: "Dupla chance" } },
  draw_no_bet: { o: 6, t: {
    es: "Empate no válido", en: "Draw No Bet",
    pt: "Empate anula aposta" } },
  btts: { o: 7, t: {
    es: "Ambos anotan", en: "Both Teams to Score", pt: "Ambas marcam" } },
  correct_score: { o: 8, t: {
    es: "Resultado exacto", en: "Correct Score", pt: "Resultado exato" } },
  halftime_fulltime: { o: 9, t: {
    es: "Entretiempo / Final", en: "Half Time / Full Time",
    pt: "Intervalo / Final" } },
  to_qualify: { o: 10, t: {
    es: "Clasifica", en: "To Qualify", pt: "Classifica" } },
  // Líneas alternativas
  alternate_totals: { o: 11, t: {
    es: "Más / Menos (alt.)", en: "Over / Under (alt.)",
    pt: "Mais / Menos (alt.)" } },
  alternate_spreads: { o: 12, t: {
    es: "Hándicap (alt.)", en: "Handicap (alt.)", pt: "Handicap (alt.)" } },
  team_totals: { o: 13, t: {
    es: "Goles por equipo", en: "Team Goals", pt: "Gols por equipe" } },
  alternate_team_totals: { o: 14, t: {
    es: "Goles por equipo (alt.)", en: "Team Goals (alt.)",
    pt: "Gols por equipe (alt.)" } },
  // Primer tiempo
  h2h_h1: { o: 15, t: {
    es: "Ganador 1er tiempo", en: "1st Half Winner",
    pt: "Vencedor 1º tempo" } },
  totals_h1: { o: 16, t: {
    es: "Más / Menos 1er tiempo", en: "1st Half Over / Under",
    pt: "Mais / Menos 1º tempo" } },
  spreads_h1: { o: 17, t: {
    es: "Hándicap 1er tiempo", en: "1st Half Handicap",
    pt: "Handicap 1º tempo" } },
  btts_h1: { o: 18, t: {
    es: "Ambos anotan 1er tiempo", en: "1st Half Both Teams to Score",
    pt: "Ambas marcam 1º tempo" } },
  double_chance_h1: { o: 19, t: {
    es: "Doble oport. 1er tiempo", en: "1st Half Double Chance",
    pt: "Dupla chance 1º tempo" } },
  correct_score_h1: { o: 20, t: {
    es: "Resultado exacto 1er T.", en: "1st Half Correct Score",
    pt: "Resultado exato 1º tempo" } },
  // Córners
  alternate_totals_corners: { o: 21, t: {
    es: "Córners · Más / Menos", en: "Corners · Over / Under",
    pt: "Escanteios · Mais / Menos" } },
  alternate_spreads_corners: { o: 22, t: {
    es: "Córners · Hándicap", en: "Corners · Handicap",
    pt: "Escanteios · Handicap" } },
  alternate_team_totals_corners: { o: 23, t: {
    es: "Córners por equipo", en: "Team Corners",
    pt: "Escanteios por equipe" } },
  corners_1x2: { o: 24, t: {
    es: "Quién patea más córners", en: "Most Corners",
    pt: "Quem bate mais escanteios" } },
  // Tarjetas
  alternate_totals_cards: { o: 25, t: {
    es: "Tarjetas · Más / Menos", en: "Cards · Over / Under",
    pt: "Cartões · Mais / Menos" } },
  alternate_spreads_cards: { o: 26, t: {
    es: "Tarjetas · Hándicap", en: "Cards · Handicap",
    pt: "Cartões · Handicap" } },
  // Jugadores
  player_goal_scorer_anytime: { o: 27, t: {
    es: "Anota en el partido", en: "Anytime Goalscorer",
    pt: "Marca no jogo" } },
  player_first_goal_scorer: { o: 28, t: {
    es: "Primer goleador", en: "First Goalscorer",
    pt: "Primeiro a marcar" } },
  player_last_goal_scorer: { o: 29, t: {
    es: "Último goleador", en: "Last Goalscorer",
    pt: "Último a marcar" } },
  player_to_receive_card: { o: 30, t: {
    es: "Jugador que ve tarjeta", en: "Player to be Carded",
    pt: "Jogador a receber cartão" } },
  player_to_receive_red_card: { o: 31, t: {
    es: "Jugador que ve roja", en: "Player to be Sent Off",
    pt: "Jogador a receber vermelho" } },
  player_shots_on_target: { o: 32, t: {
    es: "Tiros al arco", en: "Shots on Target", pt: "Chutes a gol" } },
  player_shots: { o: 33, t: {
    es: "Remates", en: "Shots", pt: "Chutes" } },
  player_assists: { o: 34, t: {
    es: "Asistencias", en: "Assists", pt: "Assistências" } },
};

// Las palabras de las selecciones que SÍ son etiqueta. Todo lo que no está
// acá —nombres de equipo, de jugador, marcadores— es contenido y pasa igual.
const PALABRAS = {
  Draw: { es: "Empate", en: "Draw", pt: "Empate" },
  Yes: { es: "Sí", en: "Yes", pt: "Sim" },
  No: { es: "No", en: "No", pt: "Não" },
};

// "Over 2.5" / "Under 2.5" -> el molde del idioma, con la línea intacta.
const LINEAS = {
  Over: { es: "Más de", en: "Over", pt: "Mais de" },
  Under: { es: "Menos de", en: "Under", pt: "Menos de" },
};

const cual = (idioma) => idiomaSoportado(idioma) || PREDETERMINADO;

/** El título de un mercado en el idioma del jugador.
 *
 * Un mercado que el diccionario no conoce se dibuja legible —`player_shots`
 * como "Player Shots"— en vez de con guiones bajos. Se conserva el repliegue
 * que ya tenía `tituloMercado`: es lo que evita que el jugador lea una clave
 * técnica el día que GR8 emita un mercado nuevo. */
export function tituloMercado(clave, idioma) {
  const mercado = MERCADOS[clave];
  if (mercado) return mercado.t[cual(idioma)];
  return String(clave || "").replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** En qué posición va el mercado. Lo desconocido, al final. */
export function ordenMercado(clave) {
  return MERCADOS[clave]?.o ?? 99;
}

/** La etiqueta larga de una selección, para la lista de mercados.
 *
 * `nombre` es el nombre CRUDO del emisor y se usa sólo para decidir qué
 * dibujar: no se devuelve como clave ni se guarda. */
export function etiquetaDeSeleccion(nombre, home, away, idioma) {
  const texto = String(nombre ?? "");
  const i = cual(idioma);

  if (PALABRAS[texto]) return PALABRAS[texto][i];
  // Los nombres propios salen antes que cualquier molde: un equipo que se
  // llamara "Over 2" tiene que seguir siendo su nombre.
  if (texto === home || texto === away) return texto;

  let m = texto.match(/^Over\s*([\d.]+)$/i);
  if (m) return `${LINEAS.Over[i]} ${m[1]}`;
  m = texto.match(/^Under\s*([\d.]+)$/i);
  if (m) return `${LINEAS.Under[i]} ${m[1]}`;
  // "Equipo -1.5": el equipo y el signo quedan como están en los tres
  // idiomas; lo único que se normaliza es el espacio.
  m = texto.match(/^(.+?)\s*([+-][\d.]+)$/);
  if (m) return `${m[1]} ${m[2]}`;
  return texto;
}

/** La etiqueta corta, para la grilla de cuotas del sitio.
 *
 * `1`, `X` y `2` no se traducen: se leen igual en todos los idiomas en los que
 * se apuesta, y cambiarlos rompería el reconocimiento de una grilla que el
 * jugador ya conoce. */
export function etiquetaCorta(nombre, home, away, idioma) {
  const texto = String(nombre ?? "");
  const i = cual(idioma);

  if (texto === home) return "1";
  if (texto === away) return "2";
  if (texto === "Draw") return "X";
  if (PALABRAS[texto]) return PALABRAS[texto][i];
  // El espacio que queda ("Over 2.5" -> "+ 2.5") se conserva a propósito: es
  // lo que el sitio dibuja hoy, y esta unidad unifica los diccionarios sin
  // cambiar la pantalla. Apretarlo sería un cambio visual que nadie pidió,
  // mezclado con uno que sí.
  return texto.replace("Over", "+").replace("Under", "−");
}
